import { gql } from 'apollo-angular';
import { Paginator, PaginatorInfo } from '../../../types/paginator';
import OwnedEntity from '../../../interfaces/owned-entity';
import DocType from '../../../interfaces/doc-type';
import TaxReturnDifficultyLevel from '../../../interfaces/tax-return-difficulty-levels';
import DocumentTypeRequirement from '../../../interfaces/document-type-requirement';
import ID from '../../../types/id';
import SqlOperator from '../../../enums/sql-operator';
import { Direction, RelationOrderBy } from '../../../types/relation-order-by';
import DocGroupLog from "../../../interfaces/doc-group-log";

const oEFields = `
id
            name
            tax_return_difficulty_level_id
            fund{
                id
                name
                close_date
            }
            project {
              id
              name
              projectDetail{
                id
                fully_disposed
              }
              dispositions{
                id
                dispositionPipelineDates{
                  id
                  date
                  pipelineMilestone{
                   id
                   milestone{
                    id
                    name
                    }
                  }
                }
              }

              pipelineProjects(hasPipeline: {column: NAME, operator: EQ, value:"Closing"}){
                id
                pipelineProjectDates(initial_target: false){
                  id
                  date
                  pipelineMilestone{
                    id
                    milestone{
                      id
                      name
                    }
                  }
                }
              }
            }
            documentTypeRequirements {
              id
              requirement_flag
              doc_type_id
              doc_subtype_id
              due_date
              modified
              user {
                id
                person {
                    id
                    name
                }
              }
            }
`;

type GetRequirementsResults = {
  ownedEntitiesPaged: Paginator<OwnedEntity, PaginatorInfo>;
  docTypes: DocType[];
  taxReturnDifficultyLevels: TaxReturnDifficultyLevel[];
  newRowsRuns: DocGroupLog[];
  massUpdateRuns: DocGroupLog[];
};
type GetRequirementsVariables = {};
export const GET_REQUIREMENTS = gql<
  GetRequirementsResults,
  GetRequirementsVariables
>`
    query getDocRequirements {
        ownedEntitiesPaged(page:1, first: 50, hasDocumentTypeRequirements: {column: REQUIREMENT_FLAG, operator: IS_NOT_NULL},order: [{column: NAME, order: ASC}]){
           data{
            ${oEFields}
           }
           paginatorInfo {
             currentPage
             total
             hasMorePages
             perPage
           }
        }
        docTypes {
          id
          name
          permissions{
            _read
            _edit
            _delete
          }
          docSubtypes {
            id
            name
            sort_order
          }
        }
        taxReturnDifficultyLevels {
          id
          difficulty_level
        }
      newRowsRuns: docGroupLogs(sort: {column: CREATED, order: DESC}, sproc_name: "sp_create_new_tax_season_rows") {
        id
        user {
          id
          person {
            id
            name
          }
        }
        sproc_name
        doc_date
        created
      }
      massUpdateRuns: docGroupLogs(sort: {column: CREATED, order: DESC}, sproc_name: "sp_tax_season_mass_update") {
        id
        user {
          id
          person {
            id
            name
          }
        }
        sproc_name
        doc_date
        created
      }
    }
`;
export type RequirementsFilter = {
  tax_return_difficulty_level_id?: ID;
};
export type OwnedEntitiesRelationMap = {
  OR?: OwnedEntitiesRelationMap[];
  AND?: OwnedEntitiesRelationMap[];
  column?: 'RELATED_NAME' | 'OWNED_ENTITY_NAME';
  operator?: SqlOperator;
  value?: any;
};
type RefreshRequirementsResults = {
  ownedEntitiesPaged: Paginator<OwnedEntity, PaginatorInfo>;
  docTypes: DocType[];
};
type RefreshRequirementsVariables = {
  page: number;
  hasOwnedEntitiesMap?: OwnedEntitiesRelationMap | null;
  input: RequirementsFilter;
  hasTypeRequirements:
    | { column: 'REQUIREMENT_FLAG'; operator: SqlOperator.IS_NOT_NULL }
    | { column: 'OWNED_ENTITY_ID'; operator: SqlOperator.IS_NOT_NULL };
  order: RequirementsOrdering[];
};

export type RequirementsOrdering = {
  column?: string;
  taxReturnDifficultyLevel?: RelationOrderBy<string>;
  documentTypeRequirements?: RelationOrderBy<string>;
  order: Direction;
};
export const REFRESH_REQUIREMENTS = gql<
  RefreshRequirementsResults,
  RefreshRequirementsVariables
>`
  query refreshRequirements($page: Int, $hasTypeRequirements: QueryOwnedEntitiesPagedHasDocumentTypeRequirementsWhereHasConditions $hasOwnedEntitiesMap: QueryOwnedEntitiesPagedHasVOwnedEntitiesMapWhereHasConditions, $input: OwnedEntityFilter, $order: [QueryOwnedEntitiesPagedOrderRelationOrderByClause!]) {
    ownedEntitiesPaged(page:$page, first: 50, hasDocumentTypeRequirements:$hasTypeRequirements ,hasVOwnedEntitiesMap: $hasOwnedEntitiesMap, filter: $input, order: $order){
      data{
        ${oEFields}
      }
      paginatorInfo {
        currentPage
        total
        hasMorePages
        perPage
      }
    }
  }
`;

type InitializeDocumentsResult = {
  initializeDocuments: string;
};
type InitializeDocumentsVariables = {
  year: string | null;
};
export const INITIALIZE_DOCUMENTS = gql<
  InitializeDocumentsResult,
  InitializeDocumentsVariables
>`
  mutation initializeDocuments($year: String) {
    initializeDocuments(year: $year)
  }
`;

type RefreshRequirementsDataResult = {
  refreshDocumentRequirements: string | null;
};
type RefreshRequirementsDataVariables = {};

export const REFRESH_REQUIREMENTS_DATA = gql<
  RefreshRequirementsDataResult,
  RefreshRequirementsDataVariables
>`
  mutation refreshRequirementsData {
    refreshDocumentRequirements
  }
`;

export type UpsertDocRequirementInput = {
  doc_type_id: ID;
  doc_subtype_id: ID;
  owned_entity_id: ID;
  requirement_flag?: boolean | null;
  due_date?: string | null;
  user_id: ID;
};

type UpsertDocumentRequirementsResult = {
  updateDocTypeRequirements: DocumentTypeRequirement;
};
type UpsertDocumentRequirementsVariables = {
  inputs: UpsertDocRequirementInput[];
};
export const UPSERT_DOC_REQUIREMENTS = gql<
  UpsertDocumentRequirementsResult,
  UpsertDocumentRequirementsVariables
>`
  mutation upsertDocTypeRequirement(
    $inputs: [UpdateDocTypeRequirementsInput!]!
  ) {
    updateDocTypeRequirements(inputs: $inputs) {
      id
      owned_entity_id
      doc_type_id
      doc_subtype_id
      due_date
      requirement_flag
    }
  }
`;

type UpdateDifficultyLevelResult = {
  updateOwnedEntity: OwnedEntity;
};
type UpdateDifficultyLevelVariables = {
  id: ID;
  tax_return_difficulty_level_id: ID | null;
};
export const UPDATE_DIFFICULTY_LEVEL = gql<
  UpdateDifficultyLevelResult,
  UpdateDifficultyLevelVariables
>`
  mutation updateDifficultyLevel(
    $id: ID!
    $tax_return_difficulty_level_id: ID
  ) {
    updateOwnedEntity(
      id: $id
      tax_return_difficulty_level_id: $tax_return_difficulty_level_id
    ) {
      id
    }
  }
`;
